/**
 * Pure parser for free-text lab reports → structured lab results.
 * Handles tab-delimited, multi-line (name / value / ref-range), and single-line formats.
 */

export interface LabResult {
  test: string;
  value: string | number;
  unit: string;
  referenceRange: string;
  flag: string;
}

/** Parse raw lab-report text into structured results. Pure — no side effects. */
export function parseLabResults(message: string): LabResult[] {
  const normalized = message.replace(/\t/g, '\n'); // tabs to newlines
  const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean);
  const labResults: LabResult[] = [];

  // Skip known non-test lines
  const skipPatterns = /^(Your Value|Reference Range|See More|Notes:|Blog|Media|About|Careers|Contact|FAQs|Membership|Medical|Privacy|Practice|Sponsored|Hi |Please|We invite|If your|If you|Take care|The One|CMP|Lipids|CBC|Hemoglobin A1c$)/i;

  const getFlag = (val: number, refStr: string): string => {
    const r = refStr.replace(/[–—]/g, '-').match(/([\d.]+)-([\d.]+)/);
    if (r) return val < parseFloat(r[1]) ? 'low' : val > parseFloat(r[2]) ? 'high' : 'normal';
    if (refStr.match(/^>/)) return val < parseFloat(refStr.slice(1)) ? 'low' : 'normal';
    if (refStr.match(/^</)) return val > parseFloat(refStr.slice(1)) ? 'high' : 'normal';
    return 'normal';
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (skipPatterns.test(line)) continue;

    // Check: is this a test name followed by a numeric value line?
    const isName = /^[A-Za-z(]/.test(line) && !/^[\d.]/.test(line);
    const next = lines[i + 1] || '';
    const next2 = lines[i + 2] || '';
    const valMatch = next.match(/^([\d.]+)\s+(.*)/);

    if (isName && valMatch) {
      const val = parseFloat(valMatch[1]);
      const unit = valMatch[2].trim();
      const refMatch = next2.match(/^([<>]?[\d.]+[–\-][\d.]+)\s*(.*)/);
      const refStr = refMatch ? refMatch[1].replace(/[–—]/g, '-') + (refMatch[2] ? ' ' + refMatch[2] : '') : '';
      labResults.push({ test: line, value: val, unit, referenceRange: refStr.trim(), flag: getFlag(val, refStr) });
      i += refMatch ? 2 : 1;
      continue;
    }

    // Single-line: "Glucose  84.0 mg/dL  70-99 mg/dL"
    const sl = line.match(/^(.+?)\s+([\d.]+)\s+(\S+)\s+([\d.<>]+[–\-][\d.<>]+\s*\S*)/);
    if (sl) {
      const val = parseFloat(sl[2]);
      const refStr = sl[4].replace(/[–—]/g, '-');
      labResults.push({ test: sl[1].trim(), value: val, unit: sl[3], referenceRange: refStr, flag: getFlag(val, refStr) });
    }
  }

  return labResults;
}
